import { Pie } from '@ant-design/plots';
import { Table, Button, Space, Tooltip, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, PlusSquareOutlined, MinusSquareOutlined } from '@ant-design/icons';
 import utils from '../Common/utils';
import MoneyDisplay from '../Common/MoneyDisplay';
import ColorSquare from '../Common/ColorSquare';
import ShowIntentsBtn from '../Common/ShowIntentsBtn';
import { Link } from 'react-router-dom';

const { Text } = Typography;

// An antd small Button is 24x24; the empty slot has to match it exactly.
const ACTION_SLOT = { display: 'inline-flex', width: 24, height: 24 };

/**
 * One row action, occupying a fixed slot.
 *
 * When the action does not apply the button is not rendered at all, but its
 * space is held by an empty span -- so the remaining icons stay on the same
 * vertical lines down the column instead of sliding around row to row.
 */
const ActionBtn = ({ title, icon, available, onClick }) => {
  if (!available) return <span style={ACTION_SLOT} aria-hidden="true" />;
  return (
    <Tooltip title={title}>
      <Button size="small" icon={icon} onClick={onClick} />
    </Tooltip>
  );
};

const COLORS = [
    '#D22C2C', '#D26E2C', '#D2B12C', '#B1D22C', '#6ED22C',
    '#2CD22C', '#2CD26E', '#2CD2B1', '#2CB1D2', '#2C6ED2',
    '#2C2CD2', '#6E2CD2', '#B12CD2', '#D22CB1', '#D22C6E',
    '#D88C8C', '#D8AA8C', '#D8C98C', '#C9D88C', '#AAD88C',
    '#8CD88C', '#8CD8AA', '#8CD8C9', '#8CC9D8', '#8CAAD8',
    '#8C8CD8', '#AA8CD8', '#C98CD8', '#D88CC9', '#D88CAA',
]


// `colorField` below IS the colour, so two slices sharing one colour would be
// treated as the same category. The hand-picked palette holds 30; flat tag mode
// can produce far more, so past that we spread hues evenly instead of repeating.
const paletteFor = (n) =>
  n <= COLORS.length
    ? COLORS
    : Array.from({ length: n }, (_, i) => `hsl(${Math.round((360 * i) / n)}, 62%, 52%)`);


const BasePieChartWithTable = ({ data: rawData = [], ...props }) => {

  const palette = paletteFor(rawData.length);
  const data = rawData.map((x, idx) => ({
    ...x,
    color: palette[idx % palette.length],
  }));

  const amountSum = data.reduce((acc, item) => acc + item.amount, 0);


  const tableCols = [
      {
          title: 'Barva',
          dataIndex: 'color',
          key: 'color',
          render: color => <ColorSquare color={color} />
      },
      {
          title: '%',
          dataIndex: 'percentage',
          key: 'percentage',
          render: x => <b>{x} %</b>
      },
      {
          title: props.aggColTitle,
          dataIndex: 'title',
          key: 'title',
          // Flat tag mode sends `path_title` ("Vozila / kombi / kombi-servis"),
          // since sibling subtrees reuse leaf titles.
          render: (title, record) => record.path_title || title,
      },
      {
          title: 'Znesek',
          dataIndex: 'amount',
          key: 'amount',
          render: x => <MoneyDisplay type={props.moneyType} val={x} />
      },
      {
          // All row actions share one column -- three narrow columns cost width
          // the chart cards do not have.
          title: '',
          key: 'actions',
          align: 'right',
          width: 1,
          render: (_, record) => {
            // NOTE: deliberately not `dataIndex: 'id'`. The N/A bucket's id is
            // null, which URLSearchParams stringifies to "" -- the filter then
            // looks empty and gets stripped, silently showing ALL transactions.
            // The backend hands us `filter_value` instead.
            const val = record.filter_value ?? record.id ?? utils.NO_VALUE;
            const canExpand = !!record.has_children;

            // ONE slot for open/close -- a row is either openable or closable,
            // never both at once, so the +/- share a position and the icon
            // simply swaps in place when clicked. Priority:
            //   1. this row is open        -> close it
            //   2. this row can be opened  -> open it
            //   3. this row only exists because its PARENT was opened -> fold
            //      that parent back (the parent may have no row of its own).
            let toggle = null;
            if (record.is_expanded) {
              toggle = {
                id: record.id,
                icon: <MinusSquareOutlined />,
                title: 'Združi nazaj v eno kategorijo',
              };
            } else if (canExpand) {
              toggle = {
                id: record.id,
                icon: <PlusSquareOutlined />,
                title: 'Razčleni to kategorijo tukaj',
              };
            } else if (record.collapse_into_id != null) {
              toggle = {
                id: record.collapse_into_id,
                icon: <MinusSquareOutlined />,
                title: `Združi nazaj v „${record.collapse_into_title}“`,
              };
            }

            return (
              <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                {props.onToggleExpand && (
                  <ActionBtn
                    available={!!toggle}
                    icon={toggle?.icon}
                    title={toggle?.title}
                    onClick={() => props.onToggleExpand(toggle.id)}
                  />
                )}
                {/* Drill: replace the whole chart with this category's contents.
                    Down = a level deeper into the tag tree; the matching up
                    arrow above the table climbs back out. */}
                {props.onDrillDown && (
                  <ActionBtn
                    icon={<ArrowDownOutlined />}
                    available={canExpand}
                    title="Poglej samo to kategorijo"
                    onClick={() => props.onDrillDown(record)}
                  />
                )}
                <Link to={{pathname: "/intents", hash: utils.getUpdatedURL({[props.filterName]: [val]})}}>
                  <ShowIntentsBtn/>
                </Link>
              </Space>
            );
          }
      },
  ];

  const config = {
    data: data,
    angleField: 'percentage',
    innerRadius: 0.6,
    colorField: 'color',
    color: x => x.color,

    label: {
      type: 'inner',
      // Percentage alone is unreadable -- you cannot tell which slice is which
      // without cross-referencing the table. 'limit-in-shape' drops the label
      // outright on slices too small to hold it, so the chart stays legible.
      formatter: x => `${x.title ?? ''}\n${x.percentage} %`,
      style: {
        fontWeight: 'bold',
        fontSize: 10,
        textAlign: 'center',
      },
      offset: 0,
      layout: [{ type: 'limit-in-shape' }],
    },
    legend: false,
    tooltip: false,
        statistic: {
      // Top line (usually "Total" or some label)
      title: {
        // Names the scope once drilled in, so the donut says what it is showing
        // rather than just "total of something".
        formatter: () => props.currentTitle || 'Skupaj',
        style: {
          fontSize: '14px',
          lineHeight: 1,
          fill: '#999',
        },
      },
      // Bottom line (your calculated value)
      content: {
        formatter: () => utils.formatToMoney(amountSum, true),
        style: {
          fontSize: '24px',
          fontWeight: 'bold',
          fill: '#262626',
        },
      },
    }
  };
  return (
    <>
      {props.headerExtra}
      <Pie {...config} />
      {props.currentTitle && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 4px' }}>
          <Text strong>{props.currentTitle}</Text>
          {props.onGoUp && (
            <Tooltip title="Nazaj na nadrejeno kategorijo">
              <Button size="small" icon={<ArrowUpOutlined />} onClick={props.onGoUp} />
            </Tooltip>
          )}
        </div>
      )}
      <Table loading={props.loading} dataSource={data} columns={tableCols} size="small" />
    </>
  )
};
export default BasePieChartWithTable;
